import { AllHTMLAttributes, forwardRef, useImperativeHandle, useState } from 'react';
import { StyleTextField } from '@/dashboard/mui/form/TextField';
import { Autocomplete, Box, CircularProgress, FormHelperText, TextField } from '@mui/material';
import clsx from 'clsx';
import { Control, Controller, ControllerProps } from 'react-hook-form';
import { ErrorFormField } from '../types';

type CustomAutocompleteProps = AllHTMLAttributes<HTMLDivElement> &
  ErrorFormField & {
    [str: string]: any;
    optionProps?: {
      value: 'value';
      label: 'label';
    };
  };

const CustomAutocomplete = forwardRef((props: CustomAutocompleteProps, ref) => {
  const {
    className,
    label,
    error,
    errMessage,
    loading,
    onChange,
    optionProps = {
      value: 'value',
      label: 'label',
    },
    value,
    ...extraProps
  } = props as any;
  const [inputValue, setInputValue] = useState(value || '');

  /**
   * methods
   */
  const selectChangeAction = (event: any, newValue: any) => {
    setInputValue(newValue);
    if (onChange) onChange(newValue);
  };

  // update current select item
  const updateSelectItem = (item) => {
    setInputValue(item);
  };

  useImperativeHandle(ref, () => ({
    updateSelectItem,
  }));

  return (
    <Box className={clsx('flex flex-col', className)}>
      <Autocomplete
        ref={ref}
        loading={loading}
        value={inputValue}
        onChange={selectChangeAction}
        isOptionEqualToValue={(option, value) => option[optionProps.value] === value[optionProps.value]}
        getOptionLabel={(option) => option[optionProps.label]}
        {...extraProps}
        renderInput={(params) => {
          return (
            <StyleTextField
              {...params}
              label={label}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          );
        }}
      />
      {error && (
        <FormHelperText className="mt-1" error={error}>
          {errMessage}
        </FormHelperText>
      )}
    </Box>
  );
});

type ControlledProps = Omit<ControllerProps, 'control' | 'render'> &
  CustomAutocompleteProps & {
    control: Control<any>;
  };

export const AutocompletControlled = forwardRef(({ control, name, ...props }: ControlledProps, ref) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return <CustomAutocomplete {...props} value={value} onChange={onChange} error={!!error} errMessage={error?.message} />;
      }}
    />
  );
});

export default CustomAutocomplete;
